import React from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useCurrencyStore } from '../store/useCurrencyStore';
import { PublicAPI } from '../api/public';
import { normalizeMediaUrl } from '../api/base';

const Wrap = styled.section`margin:18px 0;`;
const HeadRow = styled.div`
  display:flex; flex-wrap:wrap; gap:10px; align-items:center; justify-content:space-between; margin:0 0 10px;
`;
const Controls = styled.div`
  display:flex; flex-wrap:wrap; gap:8px;
  select, input{
    border:1px solid var(--bdr); background:var(--card); border-radius:10px;
    padding:8px 10px; font-size:14px; min-width:140px;
  }
  input{ width:130px; }
  .btn, .btn-secondary{
    padding:8px 12px; border-radius:10px; border:1px solid var(--bdr); background:var(--card); box-shadow:var(--shadow);
  }
`;
const UnitsGrid = styled.div`
  display:grid; gap:12px; grid-template-columns: repeat(3, minmax(0,1fr));
  @media(max-width:1024px){ grid-template-columns: repeat(2, minmax(0,1fr)); }
  @media(max-width:640px){ grid-template-columns: 1fr; }
`;
const UnitCard = styled.div`
  border:1px solid var(--bdr); border-radius:16px; background:var(--card); box-shadow:var(--shadow);
  overflow:hidden; display:flex; flex-direction:column;
  .img{ 
    height:200px; 
    background:#f5f5f7; 
    display:block; 
    overflow:hidden; 
    position: relative;
    cursor: ${p => p.$clickable ? 'zoom-in' : 'default'};
  }
  .img img{ 
    width:100%; 
    height:100%; 
    object-fit: contain; /* Изменено с cover на contain чтобы вся планировка была видна */
    display:block; 
    background: #fff; /* Белый фон для планировок */
    transition: transform 0.2s ease;
  }
  .img:hover img {
    transform: ${p => p.$clickable ? 'scale(1.05)' : 'none'};
  }
  .body{ padding:12px; display:grid; gap:6px; }
  .title{ font-weight:800; }
  .row{ display:flex; gap:10px; flex-wrap:wrap; font-size:13px; color:var(--muted); }
  .badges{ display:flex; gap:6px; flex-wrap:wrap; }
  .badge{ border:1px solid var(--bdr); background:var(--card); border-radius:999px; padding:4px 8px; font-size:12px; line-height:1; }
`;
const MoreRow = styled.div`display:grid; place-items:center; margin-top:12px;`;

// Lightbox
const Lightbox = styled.div`
  position: fixed; inset: 0; z-index: 9999;
  background: rgba(0,0,0,.92);
  display: grid; place-items: center;
`;
const LBInner = styled.div`
  width: min(1200px, 96vw);
  height: min(92vh, 900px);
  position: relative;
`;
const LBImg = styled.img` width:100%; height:100%; object-fit:contain; display:block; `;
const LBClose = styled.button`
  position:absolute; top:12px; right:12px;
  width:42px; height:42px; border-radius:999px; border:1px solid rgba(255,255,255,.25);
  background: rgba(0,0,0,.45); color:#fff; font-size:20px; cursor:pointer; box-shadow: var(--shadow);
`;

// Нормализация ссылок медиа к единому PUBLIC_ORIGIN (убираем /api перед /uploads)
const toAbsUrl = (url='') => normalizeMediaUrl(url) || '';

export default function UnitsSection({
  slugOrId,
  pageSize = 9,
  propertyType = null, // Добавляем тип объекта для условной логики
}) {
  const { i18n, t } = useTranslation();
  const currencyCode = useCurrencyStore(s => s.currency) || 'AED';
  const formatPrice  = useCurrencyStore(s => s.formatPrice);

  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState('');
  const [units, setUnits] = React.useState([]);
  const [page, setPage] = React.useState(1);
  const [total, setTotal] = React.useState(0);

  const [lbSrc, setLbSrc] = React.useState('');
  const lbOpen = !!lbSrc;

  // Фильтры/сортировка
  const [bedrooms, setBedrooms]   = React.useState('');
  const [status, setStatus]       = React.useState('');
  const [minPrice, setMinPrice]   = React.useState('');
  const [maxPrice, setMaxPrice]   = React.useState('');
  const [isFeatured, setFeatured] = React.useState('');
  const [sort, setSort]           = React.useState('price_asc');

  const lang = i18n.language || 'ru';

  const load = React.useCallback(async (opts={ append:false, toPage: undefined }) => {
    const nextPage = opts.toPage ?? page;
    setLoading(true); setError('');
    try{
      const data = await PublicAPI.propertyUnits(slugOrId, {
        locale: lang,
        currency: currencyCode,
        page: String(nextPage),
        page_size: String(pageSize),
        sort,
        ...(bedrooms ? { bedrooms: String(bedrooms) } : {}),
        ...(status ? { status } : {}),
        ...(minPrice ? { price_min: String(minPrice) } : {}),
        ...(maxPrice ? { price_max: String(maxPrice) } : {}),
        ...(isFeatured ? { is_featured: isFeatured } : {}),
      });

      const list = Array.isArray(data?.units) ? data.units : [];
      const mapped = list.map(u => {
        const img = normalizeMediaUrl(
          u.cover_thumb || u.plan_thumb ||
          u.cover_url   || u.plan_url   ||
          (Array.isArray(u.images) ? (u.images[0]?.thumbnail_url || u.images[0]?.url) : '') ||
          ''
        );
        return {
          id: String(u.id ?? u.slug ?? JSON.stringify(u)),
          title: u.title || u.name || (u.bedrooms != null ? `${u.bedrooms} BR` : (lang==='en' ? 'Unit' : 'Юнит')),
          image: toAbsUrl(img),
          price_from: u.price_from ?? u.price_min ?? u.price ?? null,
          area_from: u.area_from ?? u.area_min ?? u.area ?? u.area_total ?? u.size_from ?? null,
          bedrooms:  u.bedrooms ?? u.beds ?? null,
          property_type: u.property_type?.name || u.property_type || null,
          status: u.status || null,
        };
      });

      setUnits(prev => opts.append ? [...prev, ...mapped] : mapped);
      const totalItems = Number(data?.pagination?.total ?? data?.pagination?.count ?? mapped.length);
      setTotal(totalItems);
      if (opts.toPage !== undefined) setPage(opts.toPage);
    }catch(e){
      setError(e?.message || 'Error');
      if (!opts.append) { setUnits([]); setTotal(0); }
    }finally{
      setLoading(false);
    }
  }, [slugOrId, lang, currencyCode, page, pageSize, sort, bedrooms, status, minPrice, maxPrice, isFeatured]);

  React.useEffect(() => { load({ append:false, toPage: 1 }); }, [load]);

  // блокировка скролла при лайтбоксе
  React.useEffect(() => {
    if (!lbOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => { if (e.key === 'Escape') setLbSrc(''); };
    window.addEventListener('keydown', onKey);
    return () => { document.body.style.overflow = prev; window.removeEventListener('keydown', onKey); };
  }, [lbOpen]);

  const resetFilters = () => {
    setBedrooms(''); setStatus(''); setMinPrice(''); setMaxPrice(''); setFeatured(''); setSort('price_asc');
    setPage(1);
  };

  const canLoadMore = units.length < total;

  // Определяем является ли объект коммерческим
  const isCommercial = propertyType === 'commercial' || 
                      propertyType === 'commercial-rent' || 
                      (typeof propertyType === 'object' && 
                       (propertyType?.slug === 'commercial' || propertyType?.slug === 'commercial-rent'));

  return (
    <Wrap>
      <HeadRow>
        <h2 style={{margin:0}}>{t('units.title')}</h2>
        <Controls>
          {/* Скрываем фильтр спален для коммерческих объектов */}
          {!isCommercial && (
            <select value={bedrooms} onChange={e=>setBedrooms(e.target.value)}>
              <option value="">{t('units.filters.roomsAny')}</option>
              <option value="0">{lang==='en' ? 'Studio' : 'Студия'}</option>
              <option value="1">1 {t('units.filters.bedroomsShort')}</option>
              <option value="2">2 {t('units.filters.bedroomsShort')}</option>
              <option value="3">3 {t('units.filters.bedroomsShort')}</option>
              <option value="4">4 {t('units.filters.bedroomsShort')}</option>
            </select>
          )}

          {/* Убираем фильтр статуса */}
          {/* <select value={status} onChange={e=>setStatus(e.target.value)}>
            <option value="">{t('units.filters.statusAny')}</option>
            <option value="available">{t('units.status.available')}</option>
            <option value="reserved">{t('units.status.reserved')}</option>
            <option value="sold">{t('units.status.sold')}</option>
          </select> */}

          <input type="number" placeholder={t('units.filters.minPrice')}
                 value={minPrice} onChange={e=>setMinPrice(e.target.value)} />
          <input type="number" placeholder={t('units.filters.maxPrice')}
                 value={maxPrice} onChange={e=>setMaxPrice(e.target.value)} />


          <select value={sort} onChange={e=>setSort(e.target.value)}>
            <option value="price_asc">{t('units.sort.priceAsc')}</option>
            <option value="price_desc">{t('units.sort.priceDesc')}</option>
            <option value="area_asc">{t('units.sort.areaAsc')}</option>
            <option value="area_desc">{t('units.sort.areaDesc')}</option>
          </select>

          <button className="btn" onClick={()=>load({ append:false, toPage:1 })}>{t('units.filters.apply')}</button>
          <button className="btn-secondary" onClick={resetFilters}>{t('units.filters.reset')}</button>
        </Controls>
      </HeadRow>

      {error && <p style={{color:'#c62828', marginTop:6}}>{error}</p>}
      {loading && !units.length && <p style={{opacity:.7}}>{t('common.loading')}</p>}

      {!!units.length && (
        <>
          <UnitsGrid>
            {units.map(u => {
              const clickable = !!u.image;
              return (
                <UnitCard key={u.id} $clickable={clickable}>
                  <div className="img" onClick={() => clickable && setLbSrc(u.image)}>
                    <img
                      src={
                        u.image ||
                        'data:image/svg+xml;utf8,' + encodeURIComponent(
                          '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 600 400"><rect width="600" height="400" fill="#f4f4f6"/><text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" fill="#9aa0a6" font-family="sans-serif" font-size="20">No image</text></svg>'
                        )
                      }
                      alt={`${u.title} plan`}
                    />
                  </div>
                  <div className="body">
                    <div className="title">{u.title}</div>
                    <div className="badges">
                      {!!u.property_type && <span className="badge">{u.property_type}</span>}
                      {/* Скрываем спальни для коммерческих объектов */}
                      {!isCommercial && u.bedrooms === 0 && <span className="badge">{t('card.studio')}</span>}
                      {!isCommercial && typeof u.bedrooms === 'number' && u.bedrooms > 0 && (
                        <span className="badge">{u.bedrooms} {t('units.filters.bedroomsShort')}</span>
                      )}
                      {/* Убираем статус объекта */}
                      {/* {!!u.status && <span className="badge">{t(`units.status.${u.status}`, u.status)}</span>} */}
                    </div>
                    <div className="row">
                      {/* Скрываем спальни для коммерческих объектов */}
                      {!isCommercial && u.bedrooms!=null && <span>{t('units.labels.bedrooms')}: {u.bedrooms === 0 ? t('card.studio') : u.bedrooms}</span>}
                      {u.area_from!=null && <span>{t('units.labels.areaFrom')}: {u.area_from} {t('card.area_m2')}</span>}
                    </div>
                    <div className="row" style={{marginTop:2}}>
                      {u.price_from!=null && (
                        <span>
                          {t('units.labels.priceFrom')}: 
                          <span dangerouslySetInnerHTML={{ __html: formatPrice(Number(u.price_from)) }} />
                        </span>
                      )}
                    </div>
                  </div>
                </UnitCard>
              );
            })}
          </UnitsGrid>

          {canLoadMore && (
            <MoreRow>
              <button
                className="btn"
                disabled={loading}
                onClick={() => load({ append:true, toPage: page + 1 })}
              >
                {loading ? t('common.loading') : t('units.filters.showMore')}
              </button>
            </MoreRow>
          )}
        </>
      )}

      {lbOpen && (
        <Lightbox onClick={()=>setLbSrc('')}>
          <LBInner onClick={(e)=>e.stopPropagation()}>
            <LBClose onClick={()=>setLbSrc('')} aria-label="Закрыть">✕</LBClose>
            <LBImg src={lbSrc} alt="unit image" />
          </LBInner>
        </Lightbox>
      )}
    </Wrap>
  );
}
